/**
 * /admin/team-sync — join archive (BBB-ingest) teams to catalog teams.
 *
 * The team twin of Venue Sync. Archive ingests mint a Team row whenever no
 * SYSTEM team has the exact name, so one franchise shows up once per season
 * in the analyzer. Saving here moves the archive team's matches, team
 * sheets, innings and balls onto the catalog team and remembers the name,
 * so the next ingest lands on the catalog team by itself. Every save is
 * undoable from the "Recent" list below.
 *
 * Renamed franchises (Delhi Daredevils -> Delhi Capitals) are linked with
 * "old name" ticked: the catalog team is then shown with both names,
 * "Delhi Capitals (formerly Delhi Daredevils)".
 *
 * Only confident rows (known name / renamed / high confidence) are
 * pre-filled; everything else starts as "Leave for now".
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  RefreshCw, ArrowLeft, Loader2, AlertTriangle, CheckCircle2, X, Search,
  GitMerge, Undo2, Save, History,
} from "lucide-react";
import {
  getTeamSync, commitTeamSync, undoTeamSync,
} from "../../api/owner.api";

const LEAVE = "";
const NEW = "__new__";

const TAGS = {
  auto_from_alias: { label: "Known name", cls: "bg-sky-500/10 text-sky-300 border-sky-500/30" },
  renamed:         { label: "Renamed franchise", cls: "bg-violet-500/10 text-violet-300 border-violet-500/30" },
  high_confidence: { label: "High confidence", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  needs_review:    { label: "Needs review", cls: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
  no_match:        { label: "No match", cls: "bg-red-500/10 text-red-300 border-red-500/30" },
};

const FILTERS = [
  { value: "all", label: "All" },
  { value: "confident", label: "Confident" },
  { value: "renamed", label: "Renamed" },
  { value: "needs_review", label: "Needs review" },
  { value: "no_match", label: "No match" },
];

const isConfident = (tag) =>
  tag === "auto_from_alias" || tag === "high_confidence" || tag === "renamed";

function teamLabel(t) {
  const bits = [t.display_name || t.name];
  if (t.short_name) bits.push(t.short_name);
  if (t.category && t.category !== "MEN") bits.push(t.category);
  return bits.join(" · ");
}

export default function AdminTeamSync() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [choice, setChoice] = useState({});   // archival_id -> catalog id | NEW | LEAVE
  const [former, setFormer] = useState({});   // archival_id -> bool ("old name")
  const [newTeam, setNewTeam] = useState({}); // archival_id -> {name, short_name, team_type, team_category}
  const [saving, setSaving] = useState(false);
  const [saveErrors, setSaveErrors] = useState([]);
  const [undoing, setUndoing] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data: body } = await getTeamSync();
      setData(body);
      const init = {};
      const fm = {};
      for (const r of body.archival) {
        const top = r.suggestions[0];
        init[r.id] = isConfident(r.tag) && top ? top.id : LEAVE;
        fm[r.id] = !!(top && top.renamed && isConfident(r.tag));
      }
      setChoice(init);
      setFormer(fm);
      setNewTeam({});
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Could not load teams.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.archival.filter((r) => {
      if (filter === "confident" && !isConfident(r.tag)) return false;
      if (["renamed", "needs_review", "no_match"].includes(filter) && r.tag !== filter) return false;
      if (!q) return true;
      return r.name.toLowerCase().includes(q)
        || r.competitions.some((c) => c.name.toLowerCase().includes(q));
    });
  }, [data, filter, search]);

  const counts = useMemo(() => {
    const c = { all: 0, confident: 0, renamed: 0, needs_review: 0, no_match: 0 };
    for (const r of data?.archival || []) {
      c.all += 1;
      if (isConfident(r.tag)) c.confident += 1;
      if (r.tag in c) c[r.tag] += 1;
    }
    return c;
  }, [data]);

  const pending = useMemo(() => {
    const links = [];
    const promotes = [];
    let matches = 0;
    for (const r of data?.archival || []) {
      const v = choice[r.id];
      if (!v) continue;
      if (v === NEW) {
        const t = newTeam[r.id] || {};
        promotes.push({
          archival_id: r.id,
          name: (t.name || "").trim(),
          short_name: (t.short_name || "").trim(),
          team_type: t.team_type || "",
          team_category: t.team_category || "",
        });
      } else {
        links.push({ archival_id: r.id, catalog_id: v, former_name: !!former[r.id] });
        matches += r.match_count;
      }
    }
    return { links, promotes, matches };
  }, [data, choice, former, newTeam]);

  const incompletePromote = pending.promotes.some((p) => !p.name || !p.short_name || !p.team_type);

  function pick(row, value) {
    setChoice((c) => ({ ...c, [row.id]: value }));
    const sug = row.suggestions.find((s) => s.id === value);
    setFormer((f) => ({ ...f, [row.id]: !!(sug && sug.renamed) }));
    if (value === NEW && !newTeam[row.id]) {
      setNewTeam((t) => ({
        ...t,
        [row.id]: {
          name: row.name,
          short_name: (row.short_name || "").slice(0, 20),
          team_type: "LEAGUE",
          team_category: row.women ? "WOMEN" : "MEN",
        },
      }));
    }
  }

  function setAllConfident() {
    setChoice((c) => {
      const next = { ...c };
      for (const r of data?.archival || []) {
        if (isConfident(r.tag) && r.suggestions[0]) next[r.id] = r.suggestions[0].id;
      }
      return next;
    });
    setFormer((f) => {
      const next = { ...f };
      for (const r of data?.archival || []) {
        if (isConfident(r.tag) && r.suggestions[0]) next[r.id] = !!r.suggestions[0].renamed;
      }
      return next;
    });
  }

  function clearAll() {
    setChoice((c) => Object.fromEntries(Object.keys(c).map((k) => [k, LEAVE])));
  }

  async function save() {
    if (saving || incompletePromote) return;
    if (!pending.links.length && !pending.promotes.length) return;
    setSaving(true);
    setSaveErrors([]);
    setError("");
    try {
      const { data: res } = await commitTeamSync(pending);
      const bits = [];
      if (res.linked) bits.push(`${res.linked} team(s) linked, ${res.matches_moved} match(es) moved`);
      if (res.promoted) bits.push(`${res.promoted} new catalog team(s)`);
      setToast(`${bits.join("; ")}. Undo any of them under Recent.`);
      await load();
    } catch (e) {
      const body = e?.response?.data;
      setError(body?.detail || e.message || "Save failed.");
      setSaveErrors(body?.errors || []);
    } finally {
      setSaving(false);
    }
  }

  async function undo(entry) {
    if (undoing) return;
    setUndoing(entry.id);
    setError("");
    try {
      const { data: res } = await undoTeamSync(entry.id);
      setToast(entry.action === "link"
        ? `Undone: "${entry.archival_name}" is back on its archive team (${res.restored} match(es)).`
        : `Undone: "${entry.archival_name}" is an archive team again.`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Undo failed.");
    } finally {
      setUndoing("");
    }
  }

  const catalog = data?.catalog || [];
  const history = data?.history || [];

  return (
    <div className="p-6 space-y-4 pb-28">
      <Link
        to="/admin"
        className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary"
      >
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </Link>

      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <GitMerge className="w-6 h-6" /> Sync teams
          </h1>
          <p className="text-sm text-text-secondary mt-1 max-w-3xl">
            Ingested archive teams that aren't joined to a catalog team yet
            ({counts.all}). Pick the catalog team for each one: its matches,
            team sheets and balls move onto that team and the name is
            remembered, so the next ingest lands there by itself. For a
            renamed franchise tick <em>old name</em> and both names are shown,
            e.g. "Delhi Capitals (formerly Delhi Daredevils)". Every save can
            be undone.
          </p>
        </div>
        <button
          onClick={load}
          className="px-3 py-2 text-sm rounded-md border border-border-default hover:bg-bg-secondary flex items-center gap-1.5"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      <div className="flex items-center gap-3 flex-wrap text-sm">
        <div className="inline-flex rounded-md border border-border-default overflow-hidden" role="tablist">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              role="tab"
              aria-selected={filter === f.value}
              onClick={() => setFilter(f.value)}
              className={`px-3 py-1.5 ${filter === f.value ? "bg-bg-secondary font-medium" : "hover:bg-bg-secondary text-text-secondary"}`}
            >
              {f.label} <span className="tabular-nums text-text-secondary">({counts[f.value] ?? 0})</span>
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Team or competition…"
            aria-label="Search archive teams"
            className="pl-8 pr-3 py-1.5 rounded-md border border-border-default bg-bg-primary w-64"
          />
        </div>
        <button
          onClick={setAllConfident}
          className="px-3 py-1.5 rounded-md border border-border-default hover:bg-bg-secondary"
        >
          Pick all confident
        </button>
        <button
          onClick={clearAll}
          className="px-3 py-1.5 rounded-md border border-border-default hover:bg-bg-secondary text-text-secondary"
        >
          Clear all picks
        </button>
      </div>

      {toast && (
        <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 px-4 py-2 text-sm flex items-start justify-between gap-3">
          <span className="flex items-start gap-1.5">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{toast}</span>
          </span>
          <button onClick={() => setToast("")} aria-label="Dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-2 text-sm">
          <div className="flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> {error}</div>
          {saveErrors.length > 0 && (
            <ul className="list-disc ml-6 mt-1 text-xs">
              {saveErrors.map((m) => <li key={m}>{m}</li>)}
            </ul>
          )}
        </div>
      )}

      <div className="rounded-md border border-border-default bg-bg-primary overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">Archive teams waiting to be joined to the catalog</caption>
          <thead className="bg-bg-secondary text-left text-xs uppercase tracking-wider text-text-secondary">
            <tr>
              <th scope="col" className="px-4 py-2.5">Archive team</th>
              <th scope="col" className="px-4 py-2.5 text-right">Matches</th>
              <th scope="col" className="px-4 py-2.5">Match</th>
              <th scope="col" className="px-4 py-2.5 min-w-[24rem]">Catalog team</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-default">
            {loading && !data && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-text-secondary">
                <Loader2 className="w-5 h-5 inline-block mr-2 animate-spin" /> Loading…
              </td></tr>
            )}
            {data && rows.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-text-secondary">
                {counts.all === 0 ? "Every archive team is joined to the catalog." : "Nothing matches this filter."}
              </td></tr>
            )}
            {rows.map((r) => (
              <TeamRow
                key={r.id}
                row={r}
                value={choice[r.id] ?? LEAVE}
                onPick={(v) => pick(r, v)}
                former={!!former[r.id]}
                onFormer={(v) => setFormer((f) => ({ ...f, [r.id]: v }))}
                catalog={catalog}
                teamTypes={data?.team_types || []}
                categories={data?.categories || []}
                team={newTeam[r.id]}
                onTeam={(t) => setNewTeam((s) => ({ ...s, [r.id]: { ...s[r.id], ...t } }))}
              />
            ))}
          </tbody>
        </table>
      </div>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-text-secondary">Recent</h2>
        {history.length === 0 ? (
          <p className="text-sm text-text-secondary">Nothing synced yet.</p>
        ) : (
          <div className="rounded-md border border-border-default bg-bg-primary divide-y divide-border-default text-sm">
            {history.map((h) => (
              <div key={h.id} className={`px-4 py-2 flex items-center justify-between gap-3 ${h.undone_at ? "opacity-50" : ""}`}>
                <div className="min-w-0">
                  <span className="font-medium">{h.archival_name}</span>
                  <span className="text-text-secondary">
                    {h.action === "link"
                      ? ` → ${h.catalog_name}${h.is_former_name ? " · old name" : ""} (${h.moved} match(es) moved)`
                      : ` → new catalog team "${h.catalog_name}"`}
                  </span>
                  <div className="text-xs text-text-secondary">
                    {new Date(h.at).toLocaleString("en-IN")}{h.by ? ` · ${h.by}` : ""}
                    {h.undone_at ? " · undone" : ""}
                  </div>
                </div>
                {!h.undone_at && (
                  <button
                    onClick={() => undo(h)}
                    disabled={!!undoing}
                    className="px-2.5 py-1 text-xs rounded-md border border-border-default hover:bg-bg-secondary inline-flex items-center gap-1 disabled:opacity-50"
                  >
                    {undoing === h.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />} Undo
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {(pending.links.length > 0 || pending.promotes.length > 0) && (
        <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-border-default bg-bg-primary/95 backdrop-blur px-6 py-3 flex items-center justify-end gap-4 text-sm">
          <span className="text-text-secondary">
            {pending.links.length} link(s) · {pending.matches} match(es) will move
            {pending.promotes.length > 0 && ` · ${pending.promotes.length} new team(s)`}
            {incompletePromote && <span className="text-amber-300"> · a new team needs name, short name and type</span>}
          </span>
          <button
            onClick={save}
            disabled={saving || incompletePromote}
            className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════ */

function TeamRow({
  row, value, onPick, former, onFormer, catalog, teamTypes, categories, team, onTeam,
}) {
  const tag = TAGS[row.tag] || TAGS.needs_review;
  const suggested = new Set(row.suggestions.map((s) => s.id));
  const picked = value && value !== NEW ? catalog.find((c) => c.id === value) : null;
  const pickedSug = row.suggestions.find((s) => s.id === value);

  return (
    <tr className="align-top hover:bg-bg-secondary/40">
      <td className="px-4 py-2.5">
        <div className="font-medium flex items-center gap-2 flex-wrap">
          <span>{row.name}</span>
          {row.short_name && <span className="font-mono text-xs text-text-secondary">{row.short_name}</span>}
          {row.women && (
            <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide border border-pink-500/30 bg-pink-500/10 text-pink-300">
              women
            </span>
          )}
        </div>
        <div className="text-xs text-text-secondary mt-0.5">
          {row.competitions.map((c) => `${c.name} (${c.matches})`).join(", ")}
          {row.competition_count > row.competitions.length && ` +${row.competition_count - row.competitions.length} more`}
        </div>
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums">{row.match_count}</td>
      <td className="px-4 py-2.5">
        <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide border whitespace-nowrap ${tag.cls}`}>
          {tag.label}
        </span>
      </td>
      <td className="px-4 py-2.5 space-y-1.5">
        <select
          value={value}
          onChange={(e) => onPick(e.target.value)}
          aria-label={`Catalog team for ${row.name}`}
          className={`w-full px-2 py-1.5 rounded-md border bg-bg-primary ${value ? "border-emerald-500/50" : "border-border-default"}`}
        >
          <option value={LEAVE}>— Leave for now —</option>
          {row.suggestions.length > 0 && (
            <optgroup label="Suggested">
              {row.suggestions.map((s) => (
                <option key={s.id} value={s.id}>
                  {teamLabel(s)} — {Math.round(s.score * 100)}%
                  {s.renamed ? " (renamed franchise)" : ""}
                  {s.acronym ? " (abbreviation)" : ""}
                  {s.conflict ? " (men's/women's or age group differs)" : ""}
                </option>
              ))}
            </optgroup>
          )}
          <optgroup label="All catalog teams">
            {catalog.filter((c) => !suggested.has(c.id)).map((c) => (
              <option key={c.id} value={c.id}>{teamLabel(c)}</option>
            ))}
          </optgroup>
          <option value={NEW}>＋ Not in the catalog — make it a new catalog team</option>
        </select>

        {picked && (
          <>
            {former && (
              <div className="text-xs rounded-md border border-violet-500/30 bg-violet-500/10 text-violet-200 px-2 py-1 inline-flex items-center gap-1.5">
                <History className="w-3.5 h-3.5" />
                <span><strong>{row.name}</strong> → now <strong>{picked.name}</strong>.
                  Will show as "{picked.name} (formerly {[...(picked.former_names || []).filter((n) => n !== row.name), row.name].join(", ")})".</span>
              </div>
            )}
            <label className="flex items-center gap-1.5 text-xs text-text-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={former}
                onChange={(e) => onFormer(e.target.checked)}
              />
              "{row.name}" is an old name of {picked.name} (show both names)
              {pickedSug?.renamed && <span className="text-violet-300">· known rename</span>}
            </label>
            <div className="text-xs text-text-secondary">
              Catalog team already has {picked.match_count} match(es); {row.match_count} will join it.
            </div>
          </>
        )}

        {value === NEW && (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-1.5">
            <input
              value={team?.name || ""}
              onChange={(e) => onTeam({ name: e.target.value })}
              placeholder="Team name"
              aria-label="New team name"
              maxLength={150}
              className="px-2 py-1 rounded-md border border-border-default bg-bg-primary text-xs sm:col-span-2"
            />
            <input
              value={team?.short_name || ""}
              onChange={(e) => onTeam({ short_name: e.target.value })}
              placeholder="Short name"
              aria-label="New team short name"
              maxLength={20}
              className="px-2 py-1 rounded-md border border-border-default bg-bg-primary text-xs"
            />
            <select
              value={team?.team_type || ""}
              onChange={(e) => onTeam({ team_type: e.target.value })}
              aria-label="New team type"
              className="px-2 py-1 rounded-md border border-border-default bg-bg-primary text-xs"
            >
              <option value="">Type…</option>
              {teamTypes.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select
              value={team?.team_category || ""}
              onChange={(e) => onTeam({ team_category: e.target.value })}
              aria-label="New team category"
              className="px-2 py-1 rounded-md border border-border-default bg-bg-primary text-xs"
            >
              <option value="">Category…</option>
              {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
        )}
      </td>
    </tr>
  );
}
